import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { escreverNaUrl, lerDaUrl, type FiltrosDeChamado } from '../../../api/filtrosDeChamado'

/**
 * Quantos critérios estão estreitando a lista agora.
 *
 * Conta CRITÉRIO e não valor escolhido: marcar duas situações de prazo é um filtro de prazo, não
 * dois. É o número que vai no botão "Filtros", e com o painel recolhido ele é a única pista de que
 * a lista não está inteira — sem isso, quem abre um link filtrado por outra pessoa lê um recorte
 * achando que é o todo.
 *
 * O período de abertura conta como UM critério, e não dois, porque é uma coisa só: "abertos entre X
 * e Y". Quem informa só uma das pontas continua tendo um critério ligado.
 *
 * @param filtros critérios correntes
 * @returns quantos deles estão preenchidos
 */
export function quantidadeDeFiltros(filtros: FiltrosDeChamado): number {
  return [
    filtros.competencia !== '',
    filtros.de !== '' || filtros.ate !== '',
    filtros.grupo !== '',
    filtros.entidade !== '',
    filtros.situacaoSla.length > 0,
    filtros.origemDemanda.length > 0,
    filtros.origem.length > 0,
    filtros.q !== '',
    filtros.alertaDaIa !== '',
  ].filter(Boolean).length
}

/**
 * Os filtros vivem na URL, não em `useState`.
 *
 * Três consequências que valem o trabalho, e todas as três são de uso real da fiscalização: o
 * fiscal cola no e-mail o link exato do que está vendo — "os 7 chamados de maio com prazo
 * estourado" é um endereço, não um print; o botão "voltar" do navegador desfaz o filtro em vez de
 * sair da tela; e recarregar a página não zera a triagem.
 *
 * É o mesmo mecanismo da listagem de GMUD, e de propósito: são duas listas com o mesmo
 * comportamento para quem usa. O que não dá para compartilhar é o tipo dos critérios — os campos
 * são outros —, e um hook genérico sobre `Record<string, unknown>` custaria a checagem de tipo que
 * é justamente o que impede um filtro de virar `undefined` na query string.
 *
 * @returns os filtros correntes e as funções que os alteram
 */
export function useFiltrosNaUrl() {
  const [parametros, definirParametros] = useSearchParams()

  const filtros = useMemo(() => lerDaUrl(parametros), [parametros])

  const alterar = useCallback(
    (mudanca: Partial<FiltrosDeChamado>) => {
      // Mudar qualquer critério volta para a primeira página: senão o usuário filtra estando na
      // página 4 e recebe uma tela vazia que parece "nenhum resultado".
      const proximo = { ...filtros, ...mudanca }
      if (mudanca.pagina === undefined) {
        proximo.pagina = 1
      }
      // Empilha no histórico em vez de sobrescrever: com `replace`, o "voltar" pularia a triagem
      // inteira de uma vez e sairia da lista, que é o que se queria evitar.
      definirParametros(escreverNaUrl(proximo))
    },
    [filtros, definirParametros],
  )

  const limpar = useCallback(() => {
    definirParametros(new URLSearchParams())
  }, [definirParametros])

  const quantidade = quantidadeDeFiltros(filtros)

  return { filtros, alterar, limpar, temFiltro: quantidade > 0, quantidade }
}
